import { Badge, Button, Card, CardBody, CardHeader, Skeleton, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, ChevronLeft, Printer } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { warehouseApi, warehouseKeys } from '../api';
import { MovementHistory } from '../components/MovementHistory';
import { printPort } from '../ports/print.port';
import { useAuthStore, userHasPermission } from '../../../auth/auth-store';
import { PageHeader } from '../../../components/PageHeader';
import { formatDateTime, formatQty } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';

const STATUS_TONE = {
  active: 'success',
  shipped: 'neutral',
  consumed: 'neutral',
  empty: 'warning',
} as const;

/**
 * Pallet detail: what is on it, where it has been, and the label reprint.
 *
 * The print button calls the CROSS-MODULE `printPort` placeholder
 * (`ports/print.port.ts`) — M9 owns printing, and this page must not grow its
 * own printer client. Until M9 lands the button renders disabled with the
 * localized hint rather than firing at a route that does not exist.
 */
export function PalletDetailPage(): ReactNode {
  const { t } = useTranslation(['warehouseCore', 'common']);
  const { id = '' } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const canPrint = userHasPermission(user, permissions, ['warehouse.print']);
  const canMove = userHasPermission(user, permissions, ['warehouse.goods.move']);

  const { data, isPending, error } = useQuery({
    queryKey: warehouseKeys.pallet(id),
    queryFn: () => warehouseApi.getPallet(id),
    enabled: id.length > 0,
  });
  useLiveQuery(['pallets', 'stock'], warehouseKeys.pallet(id));

  // `data` is guaranteed non-null at the button: the header only renders after
  // the query resolves (isPending/error return earlier).
  const print = useMutation({
    mutationFn: () =>
      printPort.print({
        kind: 'pallet_label',
        payload: {
          lpn: data!.lpn,
          binCode: data!.currentBinCode,
        },
      }),
    onSuccess: () => {
      toastSuccess(t('warehouseCore:pallets.printQueued'));
    },
    onError: () => {
      toastError(new Error(t('warehouseCore:pallets.printUnavailable')));
    },
  });

  if (isPending) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-8 w-64 rounded" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Surface variant="subtle" className="p-6 text-sm text-ink-muted">
        {t('warehouseCore:pallets.empty')}
      </Surface>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumb={
          <Link to="/warehouse/pallets" className="inline-flex items-center gap-1 hover:text-ink">
            <ChevronLeft className="size-3.5" aria-hidden />
            {t('warehouseCore:pallets.title')}
          </Link>
        }
        title={t('warehouseCore:pallets.detail', { lpn: data.lpn })}
        subtitle={data.currentBinCode ? `${data.areaCode ?? ''} · ${data.currentBinCode}` : undefined}
        actions={
          <>
            {canPrint ? (
              <Button
                variant="secondary"
                size="sm"
                icon={<Printer />}
                disabled={!printPort.available}
                title={printPort.available ? undefined : t('warehouseCore:pallets.printUnavailable')}
                loading={print.isPending}
                onClick={() => {
                  print.mutate();
                }}
              >
                {t('warehouseCore:pallets.printLabel')}
              </Button>
            ) : null}
            {canMove ? (
              <Link to={`/warehouse/moves?lpn=${encodeURIComponent(data.lpn)}`}>
                <Button variant="primary" size="sm" icon={<ArrowLeftRight />}>
                  {t('warehouseCore:pallets.move')}
                </Button>
              </Link>
            ) : null}
          </>
        }
      />

      <div className="grid gap-5 lg:grid-cols-[20rem_minmax(0,1fr)]">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-base font-semibold text-ink">{data.lpn}</h2>
              <Badge tone={STATUS_TONE[data.status]}>
                {t(`warehouseCore:pallets.statuses.${data.status}`)}
              </Badge>
            </div>
          </CardHeader>
          <CardBody className="flex flex-col gap-3">
            <Field label={t('warehouseCore:pallets.sscc')} value={data.ssccAlias ?? '—'} mono />
            <Field label={t('warehouseCore:pallets.bin')} value={data.currentBinCode ?? '—'} mono />
            <Field label={t('warehouseCore:pallets.articles')} value={String(data.articleCount)} />
            <Field label={t('warehouseCore:pallets.quantity')} value={formatQty(data.totalQtyBase)} mono />
            <Field label={t('warehouseCore:pallets.created')} value={formatDateTime(data.createdAt)} />
            <Field label={t('warehouseCore:pallets.updated')} value={formatDateTime(data.updatedAt)} />
          </CardBody>
        </Card>

        <div className="flex flex-col gap-5">
          <Card>
            <CardHeader>
              <h2 className="font-display text-base font-semibold text-ink">
                {t('warehouseCore:pallets.contents')}
              </h2>
            </CardHeader>
            <CardBody>
              {data.contents.length === 0 ? (
                <Surface variant="subtle" className="p-4 text-sm text-ink-muted">
                  {t('warehouseCore:pallets.noContents')}
                </Surface>
              ) : (
                <ul className="flex flex-col gap-2">
                  {data.contents.map((line) => (
                    <li
                      key={line.stockId}
                      className="flex items-center justify-between gap-3 rounded-lg border border-surface-border px-3 py-2"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm text-ink">
                          <span className="font-mono text-xs text-ink-muted">{line.articleNo}</span>{' '}
                          {line.articleName}
                        </p>
                        <p className="truncate text-xs text-ink-muted">
                          {line.binCode}
                          {line.lotNo ? ` · ${line.lotNo}` : ''}
                          {line.bestBefore ? ` · ${line.bestBefore}` : ''}
                        </p>
                      </div>
                      <span className="shrink-0 font-mono text-sm">
                        {formatQty(line.qtyBase, { unit: line.unitCode ?? undefined })}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="font-display text-base font-semibold text-ink">
                {t('warehouseCore:pallets.history')}
              </h2>
            </CardHeader>
            <CardBody>
              <MovementHistory rows={data.history} />
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }): ReactNode {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="font-mono text-xs uppercase tracking-[0.14em] text-ink-muted">{label}</span>
      <span className={mono ? 'font-mono text-sm text-ink' : 'text-sm text-ink'}>{value}</span>
    </div>
  );
}
